import appsyncClient from "~/lib/appsyncClient";

// Maximum number of reconnection attempts before giving up
const MAX_RECONNECTION_ATTEMPTS = 5;

// Base delay for reconnection (milliseconds)
const BASE_RECONNECTION_DELAY_MS = 1000;

// Maximum delay for reconnection (milliseconds)
const MAX_RECONNECTION_DELAY_MS = 30000;

/**
 * Creates a managed subscription with auto-reconnection capabilities.
 *
 * @param {Object} options - Subscription options
 * @param {string} options.query - GraphQL subscription query
 * @param {Object} options.variables - Query variables
 * @param {Function} options.onData - Callback for data events
 * @param {Function} [options.onError] - Callback for errors
 * @param {Function} [options.onReconnecting] - Callback when reconnection starts
 * @param {Function} [options.onReconnected] - Callback when reconnection succeeds
 * @param {Function} [options.onMaxRetriesReached] - Callback when max retries reached
 * @returns {Object} - Controller with unsubscribe method
 */
export const createManagedSubscription = ({
  query,
  variables,
  onData,
  onError,
  onReconnecting,
  onReconnected,
  onMaxRetriesReached,
}) => {
  let subscription = null;
  let reconnectionAttempts = 0;
  let reconnectionTimeoutId = null;
  let isUnsubscribed = false;

  const subscribe = () => {
    if (isUnsubscribed) {
      return;
    }

    try {
      subscription = appsyncClient.graphql({ query, variables }).subscribe({
        next: ({ data }) => {
          if (!isUnsubscribed) {
            // Reset reconnection attempts on successful data
            reconnectionAttempts = 0;
            onData(data);
          }
        },
        error: (error) => {
          if (isUnsubscribed) {
            return;
          }

          // Notify about error
          if (onError) {
            onError(error);
          }

          // Attempt reconnection
          attemptReconnection();
        },
        complete: () => {
          if (isUnsubscribed) {
            return;
          }

          // Subscription ended unexpectedly, attempt reconnection
          attemptReconnection();
        },
      });
    } catch (error) {
      if (onError) {
        onError(error);
      }
      attemptReconnection();
    }
  };

  const attemptReconnection = () => {
    if (isUnsubscribed) {
      return;
    }

    reconnectionAttempts += 1;

    if (reconnectionAttempts > MAX_RECONNECTION_ATTEMPTS) {
      if (onMaxRetriesReached) {
        onMaxRetriesReached();
      }
      return;
    }

    // Notify about reconnection attempt
    if (onReconnecting) {
      onReconnecting(reconnectionAttempts);
    }

    // Calculate delay with exponential backoff and jitter
    const delay = calculateReconnectionDelay(reconnectionAttempts);

    reconnectionTimeoutId = setTimeout(() => {
      // Clean up existing subscription
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (e) {
          // Ignore unsubscribe errors
        }
        subscription = null;
      }

      subscribe();

      // Notify about successful reconnection if we get here
      if (onReconnected && !isUnsubscribed) {
        onReconnected();
      }
    }, delay);
  };

  const unsubscribe = () => {
    isUnsubscribed = true;

    // Clear any pending reconnection timeout
    if (reconnectionTimeoutId) {
      clearTimeout(reconnectionTimeoutId);
      reconnectionTimeoutId = null;
    }

    // Unsubscribe from current subscription
    if (subscription) {
      try {
        subscription.unsubscribe();
      } catch (e) {
        // Ignore unsubscribe errors
      }
      subscription = null;
    }
  };

  // Start the subscription
  subscribe();

  return {
    unsubscribe,
  };
};

/**
 * Calculates reconnection delay with exponential backoff and jitter.
 *
 * @param {number} attempt - Current attempt number (1-indexed)
 * @returns {number} - Delay in milliseconds
 */
export const calculateReconnectionDelay = (attempt) => {
  // Exponential backoff: baseDelay * 2^(attempt-1)
  const exponentialDelay = BASE_RECONNECTION_DELAY_MS * 2 ** (attempt - 1);

  // Cap at max delay
  const cappedDelay = Math.min(exponentialDelay, MAX_RECONNECTION_DELAY_MS);

  // Add jitter (0-50% of delay) to prevent thundering herd
  const jitter = Math.random() * cappedDelay * 0.5;

  return cappedDelay + jitter;
};

export default createManagedSubscription;
